import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { createShiftSeries, listMySeries, endShiftSeries, type SeriesRow } from "@/lib/series.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { PAY_RATES, type ShiftRole } from "@/lib/pay-rates";

export const Route = createFileRoute("/_authenticated/venue/series")({
  head: () => ({ meta: [{ title: "Recurring Engagements — Venue" }] }),
  component: VenueSeriesPage,
});

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function VenueSeriesPage() {
  const create = useServerFn(createShiftSeries);
  const list = useServerFn(listMySeries);
  const end = useServerFn(endShiftSeries);

  const [series, setSeries] = useState<SeriesRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: "",
    weekday: "5",
    start_time_local: "19:00",
    end_time_local: "23:00",
    role_type: "bartender" as ShiftRole,
    requires_dbs: false,
    positions_needed: "1",
    address: "",
    postcode: "",
    until_date: "",
    notes: "",
  });

  const refresh = async () => {
    const r = await list();
    setSeries(r.series);
  };
  useEffect(() => { refresh(); }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await create({
        data: {
          title: form.title || null,
          weekday: Number(form.weekday),
          start_time_local: form.start_time_local,
          end_time_local: form.end_time_local,
          role_type: form.role_type,
          requires_dbs: form.requires_dbs,
          positions_needed: Math.max(1, Number(form.positions_needed) || 1),
          address: form.address.trim(),
          postcode: form.postcode.trim(),
          notes: form.notes || null,
          until_date: form.until_date || null,
        },
      });
      toast.success(`Series created — ${r.generated} shifts scheduled.`);
      setForm({ ...form, title: "", notes: "" });
      await refresh();
    } catch (err) { toast.error(err instanceof Error ? err.message : "Could not create series."); }
    finally { setBusy(false); }
  };

  const handleEnd = async (id: string) => {
    if (!confirm("End this recurring engagement? Future generation stops; existing shifts stay live.")) return;
    try {
      await end({ data: { series_id: id } });
      toast.success("Series ended.");
      await refresh();
    } catch (err) { toast.error(err instanceof Error ? err.message : "Could not end series."); }
  };

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role="venue" />
      <main className="mx-auto max-w-5xl px-6 py-10 grid gap-10 lg:grid-cols-[420px_1fr]">
        <section>
          <p className="tracking-wide-luxury text-accent">New Series</p>
          <h1 className="mt-3 font-display text-3xl">Recurring Engagements</h1>
          <p className="mt-2 text-sm text-muted-foreground">Post the same shift every week. We generate the next 8 weeks and top up automatically.</p>
          <span className="gold-divider mt-4" />

          <form onSubmit={submit} className="mt-8 space-y-5 border border-border bg-card p-6">
            <div>
              <Label htmlFor="title">Title (optional)</Label>
              <Input id="title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Friday Cocktail Service" className="mt-2" />
            </div>
            <div>
              <Label htmlFor="wd">Day of week</Label>
              <select id="wd" value={form.weekday} onChange={(e) => setForm({ ...form, weekday: e.target.value })}
                className="mt-2 w-full border border-border bg-background px-3 py-2 text-sm">
                {WEEKDAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="st">Start (local)</Label>
                <Input id="st" type="time" required value={form.start_time_local}
                  onChange={(e) => setForm({ ...form, start_time_local: e.target.value })} className="mt-2" />
              </div>
              <div>
                <Label htmlFor="et">End (local)</Label>
                <Input id="et" type="time" required value={form.end_time_local}
                  onChange={(e) => setForm({ ...form, end_time_local: e.target.value })} className="mt-2" />
              </div>
            </div>
            <div>
              <Label htmlFor="role">Role</Label>
              <select
                id="role"
                value={form.role_type}
                onChange={(e) => setForm({ ...form, role_type: e.target.value as ShiftRole })}
                className="mt-2 w-full border border-border bg-background px-3 py-2 text-sm"
              >
                {Object.values(PAY_RATES).map((r) => (
                  <option key={r.role} value={r.role}>{r.label} — £{(r.venue_pence / 100).toFixed(2)}/hr</option>
                ))}
              </select>
              <p className="mt-2 text-xs text-muted-foreground">
                Bartender receives £{(PAY_RATES[form.role_type].bartender_pence / 100).toFixed(2)}/hr (100%). A tiered platform fee (£15/£12/£10 per worker) is added per shift in the series.
              </p>
            </div>
            <div>
              <Label htmlFor="addr">Venue Address</Label>
              <Input id="addr" required value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-2" />
            </div>
            <div>
              <Label htmlFor="pc">Postcode</Label>
              <Input id="pc" required value={form.postcode}
                onChange={(e) => setForm({ ...form, postcode: e.target.value.toUpperCase() })} className="mt-2" />
            </div>
            <div>
              <Label htmlFor="pn">Staff Needed</Label>
              <Input id="pn" type="number" min="1" max="50" required value={form.positions_needed}
                onChange={(e) => setForm({ ...form, positions_needed: e.target.value })} className="mt-2" />
            </div>
            <div>
              <Label htmlFor="until">Run Until (optional)</Label>
              <Input id="until" type="date" value={form.until_date}
                onChange={(e) => setForm({ ...form, until_date: e.target.value })} className="mt-2" />
              <p className="mt-2 text-xs text-muted-foreground">Leave blank to run indefinitely; we always maintain 8 weeks ahead.</p>
            </div>
            <div className="flex items-center justify-between border-t border-border pt-4">
              <div>
                <Label htmlFor="dbs2" className="cursor-pointer">Requires DBS</Label>
              </div>
              <Switch id="dbs2" checked={form.requires_dbs} onCheckedChange={(v) => setForm({ ...form, requires_dbs: v })} />
            </div>
            <div>
              <Label htmlFor="notes2">Brief (optional)</Label>
              <Textarea id="notes2" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2" />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={busy} className="flex-1 rounded-none bg-foreground text-background hover:bg-foreground/90 tracking-wide-luxury py-5">
                {busy ? "Creating…" : "Create Series"}
              </Button>
              <Link to="/venue" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent self-center">Back</Link>
            </div>
          </form>
        </section>

        <section>
          <p className="tracking-wide-luxury text-accent">Active</p>
          <h2 className="mt-3 font-display text-3xl">Your Series</h2>
          <span className="gold-divider mt-4" />
          <div className="mt-6 space-y-3">
            {series.length === 0 && (
              <div className="border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
                No recurring series yet. Create one on the left.
              </div>
            )}
            {series.map((s) => (
              <article key={s.id} className="border border-border bg-card p-5 flex justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <p className="font-display text-lg">{s.title ?? `${WEEKDAYS[s.weekday]} Engagement`}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Every {WEEKDAYS[s.weekday]} · {s.start_time_local.slice(0, 5)}–{s.end_time_local.slice(0, 5)} · £{Number(s.hourly_rate).toFixed(2)}/hr · {s.positions_needed} staff
                  </p>
                  {s.address && <p className="mt-1 text-xs text-muted-foreground">{[s.address, s.postcode].filter(Boolean).join(" · ")}</p>}
                  {s.until_date && <p className="mt-1 text-[0.65rem] text-muted-foreground tracking-wide-luxury">Runs until {s.until_date}</p>}
                  {s.last_generated_through && (
                    <p className="mt-1 text-[0.65rem] text-muted-foreground tracking-wide-luxury">Generated through {s.last_generated_through}</p>
                  )}
                </div>
                <div className="self-start">
                  {s.active ? (
                    <Button onClick={() => handleEnd(s.id)} variant="outline" className="rounded-none border-border tracking-wide-luxury text-xs">
                      End Series
                    </Button>
                  ) : (
                    <span className="border border-border text-muted-foreground px-3 py-1 tracking-wide-luxury text-[0.65rem]">Ended</span>
                  )}
                </div>
              </article>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
